import { ArrowUpRight, Check } from 'lucide-react'
import Reveal from './Reveal'

const GBP_POINTS = [
  'Profile setup or clean-up',
  'Service and category optimisation',
  'Google Posts written and published',
  'Review monitoring and response drafts',
  'Local search performance reporting',
]

const MAINTENANCE_POINTS = [
  'Hosting and uptime monitoring',
  'Small content updates on request',
  'Security and technical checks',
  'Monthly blog content available',
  'Clear monthly reporting',
]

const SUBURBS = [
  'Melbourne CBD', 'South Melbourne', 'Port Melbourne', 'Richmond', 'Collingwood',
  'Fitzroy', 'Brunswick', 'Preston', 'Coburg', 'Footscray', 'Williamstown',
  'St Kilda', 'Prahran', 'Hawthorn', 'Camberwell', 'Box Hill', 'Dandenong',
  'Frankston', 'Werribee',
]

const scrollToContact = () => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })

const LocalSEO = () => {
  return (
    <section id="local-seo" className="relative overflow-hidden bg-cream-deep/50 py-24 lg:py-32">
      <div className="mx-auto max-w-container px-5 lg:px-8">
        <Reveal className="max-w-2xl">
          <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl">
            Built around how Melbourne actually searches.
          </h2>
          <p className="mt-6 text-lg text-ink-soft">
            Your website should answer what customers already want to know: what you do, where you
            work, what it costs, and how to reach you. I set up the local search foundations to match.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {/* GBP — dark card */}
          <Reveal>
            <div id="google-business-profile" className="flex h-full flex-col rounded-xl2 bg-ink p-8 text-cream shadow-card lg:p-10">
              <h3 className="font-display text-2xl font-bold sm:text-3xl">Google Business Profile, sorted</h3>
              <p className="mt-4 text-cream/70">
                When someone nearby searches for your service, your profile is often the first thing
                they see. I clean it up, optimise it, and keep it active so you show up and look the part.
              </p>
              <ul className="mt-7 space-y-3">
                {GBP_POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-[0.96rem] text-cream/90">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-gold-bright" strokeWidth={2.5} />
                    {p}
                  </li>
                ))}
              </ul>
              <button onClick={scrollToContact} className="group mt-auto inline-flex items-center gap-1.5 pt-8 font-semibold text-gold-bright">
                Request a profile audit
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </Reveal>

          {/* Maintenance — light card */}
          <Reveal delay={0.08}>
            <div id="website-maintenance" className="flex h-full flex-col rounded-xl2 bg-paper p-8 shadow-card ring-1 ring-[var(--line)] lg:p-10">
              <h3 className="font-display text-2xl font-bold text-ink sm:text-3xl">Maintenance that keeps it fresh</h3>
              <p className="mt-4 text-ink-soft">
                A website should not go stale after launch. I keep yours updated, monitored, and ready
                for new content when your services, prices, or team change.
              </p>
              <ul className="mt-7 space-y-3">
                {MAINTENANCE_POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-[0.96rem] text-ink-soft">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-gold" strokeWidth={2.5} />
                    {p}
                  </li>
                ))}
              </ul>
              <button onClick={scrollToContact} className="group mt-auto inline-flex items-center gap-1.5 pt-8 font-semibold text-gold">
                Ask about maintenance
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </Reveal>
        </div>

        {/* Honest note */}
        <Reveal className="mt-10">
          <p className="max-w-2xl text-[0.95rem] text-ink-faint">
            No one can honestly promise a number one ranking. The goal is a stronger local search
            foundation that keeps improving over time, not a guarantee that sounds good and never lands.
          </p>
        </Reveal>
      </div>

      {/* Suburbs marquee */}
      <div className="mt-16 border-y border-[var(--line)] py-5">
        <p className="mb-4 px-5 text-center text-[0.78rem] font-semibold uppercase tracking-[0.18em] text-ink-faint lg:px-8">
          Working with businesses across Melbourne
        </p>
        <div className="relative flex overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_8%,#000_92%,transparent)]">
          <div className="flex shrink-0 animate-marquee items-center gap-3 pr-3">
            {[...SUBURBS, ...SUBURBS].map((s, i) => (
              <span key={i} className="whitespace-nowrap rounded-full border border-[var(--line)] bg-paper px-4 py-2 text-sm font-medium text-ink-soft">
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default LocalSEO
